<template>
  <div
    style="margin-bottom: 200px"
    v-if="!isLoading && userState.user.profile && testState.researcher"
  >
    <div
      style="display: flex; align-items: center; justify-content: space-between"
    >
      <div>
        <h1 v-bg-typography="'h1'">
          {{
            `Hi, ${
              userState.user.profile.first_name
                ? userState.user.profile.first_name
                : "Braingineer"
            }!`
          }}
        </h1>
        <p v-bg-typography="'body-m'">
          Here are the highlights for the
          <span
            ><b>{{ testState.name }}</b></span
          >
          test.
          <a
            href="https://braingineers.crunch.help/dashboard-faq/test-overview"
            target="_blank"
            class="link"
            >Read more</a
          >.
        </p>
      </div>
      <bg-button
        v-if="testState.report"
        text="download consultant summary"
        :fullWidth="true"
        style="width: 300px"
        @click="downloadReport"
      ></bg-button>
    </div>
    <div class="content">
      <div class="top">
        <div class="platform">
          <img
            :src="
              require(`@/assets/imgs/placeholder_${testState.platform}.png`)
            "
            alt=""
          />
          <div class="top-title">
            <p style="font-size: 10pt">Platform:</p>
            <p style="font-weight: 600">{{ testState.platform }}</p>
          </div>
        </div>

        <div class="consultant">
          <img
            v-if="testState.researcher && testState.researcher.profile && consultantImage(testState.researcher.profile.first_name)"
            :src="
              require(`@/assets/imgs/researchers/${testState.researcher.profile.first_name.toLowerCase()}.jpg`)
            "
            alt="consultant image"
          />
          <div class="top-title">
            <p style="font-size: 10pt">Consultant:</p>
            <p style="font-weight: 600">
              {{ testState.researcher && testState.researcher.profile ? testState.researcher.profile.first_name : '' }}
              {{ testState.researcher && testState.researcher.profile ? testState.researcher.profile.last_name : '' }}
            </p>
          </div>
        </div>

        <div class="time top-title">
          <p style="font-size: 10pt">Average time spent:</p>
          <p style="font-weight: 600">{{ timeSpent }}</p>
        </div>

        <div class="top-title">
          <p style="font-size: 10pt">Target group:</p>
          <p style="font-weight: 600">
            {{ testState.participants.length }}x {{ testState.target_group }}
            <bg-tooltip
              :dark="true"
              class="bg-field__icon-container"
              v-if="testState.target_group === 'custom'"
            >
              <template slot="content">{{ testState.custom_target }}</template>
              <template slot="target">
                <bg-icon class="bg-field__icon" name="information" />
              </template>
            </bg-tooltip>
          </p>
        </div>
        <div class="top-title">
          <p style="font-size: 10pt">Male:</p>
          <p style="font-weight: 600">
            {{
              testState.participants.filter(
                (i) => i.demographic.gender === "male"
              ).length
            }}x
          </p>
        </div>
        <div class="top-title">
          <p style="font-size: 10pt">Female:</p>
          <p style="font-weight: 600">
            {{
              testState.participants.filter(
                (i) => i.demographic.gender === "female"
              ).length
            }}x
          </p>
        </div>
      </div>

      <div class="background">
        <div class="purpose">
          <p style="font-size: 14pt; margin-bottom: 10px">
            <b>Purpose of user test</b>
          </p>
          <p class="line-clamp">{{ testState.purpose }}</p>
        </div>
        <div class="divider"></div>
        <div class="focus">
          <p style="font-size: 14pt; margin-bottom: 10px">
            <b>Focus points</b>
          </p>
          <ul>
            <li v-for="(item, index) in testState.focus_elements" :key="index">
              {{ item }}
            </li>
          </ul>
        </div>
        <div class="divider"></div>
        <div class="scenario">
          <p style="font-size: 14pt; margin-bottom: 10px"><b>Scenario</b></p>
          <p class="line-clamp">{{ testState.scenario }}</p>
        </div>
      </div>

      <div class="insights" v-if="this.insights.length > 0">
        <div class="insights-title">
          <p style="font-size: 14pt; margin-bottom: 10px">
            <b>Insights ({{ this.insights.length }})</b>
          </p>
          <p
            style="cursor: pointer"
            @click="$router.push(`/test/${$route.params.id}/insights`)"
          >
            <u>View all {{ this.insights.length }} insights</u>
          </p>
        </div>
        <div class="insights-wrapper" v-if="insights.length > 0">
          <div class="insight">
            <p class="title">
              <b>
                <span>
                  {{ this.insights[0].assigned_id }}.
                  <span v-html="this.insights[0].colourfulTitle"></span
                ></span>
              </b>
            </p>
            <p class="summary" v-html="this.insights[0].summary"></p>
            <img
              class="screenshot"
              :src="this.insights[0].highlight_url"
              alt=""
            />
            <p
              class="insight-link"
              @click="
                $router.push(
                  `/test/${$route.params.id}/insights/${insights[0].id}`
                )
              "
            >
              <u>View Insight</u>
            </p>
          </div>
          <div class="divider"></div>
          <div class="insight" v-if="insights.length > 1">
            <p class="title">
              <b>
                <span>
                  {{ this.insights[1].assigned_id }}.
                  <span v-html="this.insights[1].colourfulTitle"></span
                ></span>
              </b>
            </p>
            <p class="summary" v-html="this.insights[1].summary"></p>
            <img
              class="screenshot"
              :src="this.insights[1].highlight_url"
              alt=""
            />
            <p
              class="insight-link"
              @click="
                $router.push(
                  `/test/${$route.params.id}/insights/${insights[1].id}`
                )
              "
            >
              <u>View Insight</u>
            </p>
          </div>
        </div>
      </div>
      <div
        class="scores"
        v-if="topThree.length > 0 && testState.test_type === 'professional'"
      >
        <div class="pages">
          <div class="title">
            <p style="font-size: 14pt; margin-bottom: 25px">
              <b
                >Pages with most
                <span style="color: #ff14a9">frustration</span></b
              >
            </p>
            <p
              style="font-size: 10pt; cursor: pointer"
              @click="$router.push(`/test/${$route.params.id}/page-insights`)"
            >
              <u>view all pages</u>
            </p>
          </div>
          <div class="pages-wrapper">
            <div
              class="page"
              :style="{ width: '90%' }"
              v-if="topThree[0]"
              @click="
                $router.push({
                  path: `/test/${$route.params.id}/page-insights`,
                  query: {
                    page: 0,
                  },
                })
              "
            >
              <p><b>#1</b> {{ topThree[0].name }}</p>
              <p style="color: #ff14a9">
                <b>
                  <span v-if="topThree[0].frustrationIndex > 0">+</span
                  >{{ topThree[0].frustrationIndex }}%</b
                >
              </p>
            </div>
            <div
              class="page"
              :style="{ width: '80%' }"
              v-if="topThree[1]"
              @click="
                $router.push({
                  path: `/test/${$route.params.id}/page-insights`,
                  query: {
                    page: 1,
                  },
                })
              "
            >
              <p><b>#2</b> {{ topThree[1].name }}</p>
              <p style="color: #ff14a9">
                <b>
                  <span v-if="topThree[1].frustrationIndex > 0">+</span
                  >{{ topThree[1].frustrationIndex }}%</b
                >
              </p>
            </div>
            <div
              class="page"
              v-if="topThree[2]"
              :style="{
                width: '70%',
              }"
              @click="
                $router.push({
                  path: `/test/${$route.params.id}/page-insights`,
                  query: {
                    page: 2,
                  },
                })
              "
            >
              <p><b>#3</b> {{ topThree[2].name }}</p>
              <p style="color: #ff14a9">
                <b>
                  <span v-if="topThree[2].frustrationIndex > 0">+</span
                  >{{ topThree[2].frustrationIndex }}%</b
                >
              </p>
            </div>
          </div>
        </div>
        <div class="divider"></div>
        <div class="pages" v-if="topTags.length > 0">
          <div class="title">
            <p style="font-size: 14pt; margin-bottom: 25px">
              <b
                >Tags with most
                <span style="color: #ff14a9">frustration</span> peaks</b
              >
            </p>
            <p
              style="font-size: 10pt; cursor: pointer"
              @click="$router.push(`/test/${$route.params.id}/tags`)"
            >
              <u>view all tags</u>
            </p>
          </div>
          <div class="pages-wrapper">
            <div
              class="page"
              :style="{ width: '90%' }"
              @click="
                $router.push({
                  path: `/test/${$route.params.id}/tags`,
                  query: {
                    tag: 0,
                  },
                })
              "
            >
              <p><b>#1</b> {{ topTags[0].name }}</p>
              <p style="color: #ff14a9">
                <b>{{ topTags[0].count }} peaks</b>
              </p>
            </div>
            <div
              class="page"
              :style="{ width: '80%' }"
              @click="
                $router.push({
                  path: `/test/${$route.params.id}/tags`,
                  query: {
                    tag: 1,
                  },
                })
              "
            >
              <p><b>#2</b> {{ topTags[1].name }}</p>
              <p style="color: #ff14a9">
                <b>{{ topTags[1].count }} peaks</b>
              </p>
            </div>
            <div
              class="page"
              :style="{
                width: '70%',
              }"
              @click="
                $router.push({
                  path: `/test/${$route.params.id}/tags`,
                  query: {
                    tag: 2,
                  },
                })
              "
            >
              <p><b>#3</b> {{ topTags[2].name }}</p>
              <p style="color: #ff14a9">
                <b>{{ topTags[2].count }} peaks</b>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <bg-loader :dark="true" v-else />
</template>
<style lang="scss" scoped>
@import "./styles.scss";
</style>

<script>
import BgTypography from "@/directives/bg-typography";
import BgSelect from "@/components/bg-select";
import BgField from "@/components/bg-field";
import BgButton from "@/components/bg-button";
import BgTile from "@/components/bg-tile";
import BgIcon from "@/components/bg-icon";
import BgFilterBubble from "@/components/bg-filter-bubble";
import BgPercentage from "@/filters/bg-percentage";
import BgDetailCard from "@/components/bg-detail-card";
import BgLoader from "@/components/bg-loader";
import BgTooltip from "@/components/bg-tooltip";
import moment from "moment";
import API from "@/api";
import { mapState } from "vuex";

export default {
  name: "overview",
  components: {
    BgSelect,
    BgField,
    BgDetailCard,
    BgFilterBubble,
    BgButton,
    BgTile,
    BgLoader,
    BgIcon,
    BgTooltip,
  },
  directives: {
    BgTypography,
  },
  filters: {
    BgPercentage,
  },
  data() {
    return {
      emotions: ["frustration", "joy", "attention"],
      labels: [],
      tags: [],
      isLoading: true,
      placeholders: {
        mobile: require("@/assets/imgs/placeholder_mobile.svg"),
        desktop: require("@/assets/imgs/placeholder_desktop.svg"),
      },
      insights: [],
      researchers: {
        sam: require("@/assets/imgs/researchers/sam.jpg"),
        eltine: require("@/assets/imgs/researchers/eltine.jpg"),
        marion: require("@/assets/imgs/researchers/marion.jpg"),
      },
      journeyPages: [],
      showInsightsLink: false,
      tags: [],
    };
  },
  computed: {
    ...mapState({
      testState: (state) => state.test,
      userState: (state) => state.user,
    }),

    isTileLocked() {
      return this.testState.test_type === "essential";
    },
    timeSpent: function () {
      let time = 0;
      let amount = 0;

      this.testState.participants.forEach((el) => {
        let spent = el.ended_at - el.started_at;
        time = time + spent;
        amount = amount + 1;
      });
      var seconds = time / amount;

      function format(time) {
        // Hours, minutes and seconds
        var hrs = ~~(time / 3600);
        var mins = ~~((time % 3600) / 60);
        var secs = ~~time % 60;

        // Output like "1:01" or "4:03:59" or "123:03:59"
        var ret = "";
        if (hrs > 0) {
          ret += "" + hrs + ":" + (mins < 10 ? "0" : "");
        }
        ret +=
          "" + String(mins).padStart(2, "0") + ":" + (secs < 10 ? "0" : "");
        ret += "" + secs;
        return ret;
      }

      if (this.testState.participants.length > 0) {
        return format(seconds / 1000);
      } else {
        return "No participants";
      }
    },
    topThree: function () {
      let top = [];
      let totalFrus = 0;
      let amount = 0;

      if (this.labels.length > 0) {
        this.labels.forEach((l) => {
          if (l.total_participants > 5 && l.average_time > 0) {
            totalFrus = totalFrus + l.frustration;
            amount = amount + 1;
            top.push(l);
          }
        });
        function compare(a, b) {
          // Use toUpperCase() to ignore character casing
          const brandA = a.frustration;
          const brandB = b.frustration;

          let comparison = 0;
          if (brandA < brandB) {
            comparison = 1;
          } else if (brandA > brandB) {
            comparison = -1;
          }
          return comparison;
        }
        top.sort(compare);

        const average = totalFrus / amount;

        if (top[0]) {
          top[0].frustrationIndex = Math.floor(
            (top[0].frustration / average - 1) * 100
          );
        }
        if (top[1]) {
          top[1].frustrationIndex = Math.floor(
            (top[1].frustration / average - 1) * 100
          );
        }
        if (top[2]) {
          top[2].frustrationIndex = Math.floor(
            (top[2].frustration / average - 1) * 100
          );
        }
      }

      return top;
    },
    topTags: function () {
      let top = [];

      if (this.tags.length > 0) {
        this.tags.forEach((t) => {
          top.push({
            name: t.name,
            count: 0,
          });
          t.significant_points.forEach((sp) => {
            if (sp.emotion === "frustration") {
              top.find((el) => t.name === el.name).count =
                top.find((el) => t.name === el.name).count + 1;
            }
          });
        });

        function compare(a, b) {
          // Use toUpperCase() to ignore character casing
          const brandA = a.count;
          const brandB = b.count;

          let comparison = 0;
          if (brandA < brandB) {
            comparison = 1;
          } else if (brandA > brandB) {
            comparison = -1;
          }
          return comparison;
        }
        top.sort(compare);
      }

      return top;
    },
  },
  watch: {
    "$route.params.id": function (id) {
      this.fetchData();
    },
  },
  methods: {
    consultantImage(name) {
      try {
        require(`@/assets/imgs/researchers/${name.toLowerCase()}.jpg`);
        return true;
      } catch (e) {
        return false;
      }
    },
    downloadReport() {
      const url = this.testState.report;
      window.open(url, "_blank");
    },
    async fetchData() {
      await API.test.getInsights(this.$route.params.id).then((res) => {
        this.insights = res.data.insights.map((insight) => {
          return {
            ...insight,
            numberOfParticipants: insight.significant_points
              .map((sp) => {
                return sp.participant_id;
              })
              .filter((value, index, self) => {
                return self.indexOf(value) === index;
              }).length,
            colourfulTitle: insight.title.replace(
              /joy|attention|frustration/gi,
              this.highlightWords
            ),
          };
        });
      });
      const tags = await API.test.getTags(this.$route.params.id);
      this.tags = tags.tags;
      const res = await API.test.getLabels(this.$route.params.id);
      this.labels = res.data.labels;
      this.isLoading = false;
    },
    highlightWords(word) {
      let result;
      switch (word.toUpperCase()) {
        case "JOY":
          result = `<span style="color: var(--bg-color-green)">${word}</span>`;
          break;
        case "FRUSTRATION":
          result = `<span style="color: var(--bg-color-pink)">${word}</span>`;
          break;
        case "ATTENTION":
          result = `<span style="color: var(--bg-color-yellow)">${word}</span>`;
          break;
      }
      return result;
    },
    navigateToPage(id) {
      this.$router.push({
        path: `/test/${this.$route.params.id}/page-insights?page=${id}`,
      });
    },
    navigateToTag(id) {
      this.$router.push({
        path: `/test/${this.$route.params.id}/tags?tag=${id}`,
      });
    },
    openLink() {
      window.open(this.testState.report);
    },
  },
  created() {
    this.fetchData();
  },
};
</script>
